import { useNavigate, useLocation } from 'react-router'
import {
  FileText,
  LogOut,
  Home,
  Trophy,
  Search,
  BookCopy,
  ArrowLeft,
  FileSpreadsheet,
  Filter,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/hooks/useAuth'
import NotificationBell from '@/components/NotificationBell'
import { useState, useEffect, useRef } from 'react'
import apiClient from '@/services/api/api'
import { toast } from 'react-toastify'
import { useGetDepartments } from '@/hooks/queries/useBusinessUnits'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ALLOWED_EXPORT_EMAILS } from '@/constants/Permissions'
import { handleError } from '@/utils/helpers'

interface LibraryJDHeaderProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  isTopTrending: boolean
  onToggleTrending: () => void
  selectedBU: string
  onBUChange: (bu: string) => void
}

// Unified button base styles
const BASE_BUTTON_STYLE =
  'flex items-center gap-1.5 md:gap-2 font-semibold text-white transition-all duration-200 rounded-full shadow-lg'

const BUTTON_VARIANTS = {
  icon: `${BASE_BUTTON_STYLE} bg-primary hover:scale-110 hover:shadow-xl outline-ring/50 active:scale-95 p-2 md:p-3`,
  avatar: `${BASE_BUTTON_STYLE} bg-primary hover:scale-110 hover:shadow-xl outline-ring/50 active:scale-95 w-9 h-9 md:w-11 md:h-11 p-0 justify-center`,
}

const LibraryJDHeader = ({
  searchQuery,
  onSearchChange,
  isTopTrending,
  onToggleTrending,
  selectedBU,
  onBUChange,
}: LibraryJDHeaderProps) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isDownloading, setIsDownloading] = useState(false)

  // Check if current user can access export feature
  const canExport = user?.email && ALLOWED_EXPORT_EMAILS.includes(user.email)
  const [tempSelectedBU, setTempSelectedBU] = useState(selectedBU)
  const [buSearchQuery, setBuSearchQuery] = useState('')
  const [isBUSelectOpen, setIsBUSelectOpen] = useState(false)
  const buSearchInputRef = useRef<HTMLInputElement>(null)
  const { data: departmentsData } = useGetDepartments()

  // Sync temp selection when selectedBU changes
  useEffect(() => {
    setTempSelectedBU(selectedBU)
  }, [selectedBU])

  // Filter departments based on search query
  const filteredDepartments = (departmentsData?.departments || []).filter((dept) =>
    dept.toLowerCase().includes(buSearchQuery.toLowerCase())
  )

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isBUSelectOpen && buSearchInputRef.current) {
      setTimeout(() => {
        buSearchInputRef.current?.focus()
      }, 100)
    } else {
      // Clear search when dropdown closes
      setBuSearchQuery('')
    }
  }, [isBUSelectOpen])

  // Centralized handlers
  const handleHome = () => navigate('/')
  const handleMyJDs = () => navigate('/my-jd')
  const handleLibraryJD = () => {
    onSearchChange('') // Clear search query
    onBUChange('') // Clear BU filter
    setTempSelectedBU('') // Clear temp BU selection
    navigate('/library-jd')
  }
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Search is handled by onChange, so this just prevents form submission
  }

  const handleApplyFilter = () => {
    onBUChange(tempSelectedBU)
  }

  const handleResetFilter = () => {
    // Force clear by setting to empty string
    setTempSelectedBU('')
    onBUChange('')
    // Close the dropdown to ensure visual update
    setIsBUSelectOpen(false)
    // Clear search query
    setBuSearchQuery('')
  }

  const handleDownloadReport = async () => {
    try {
      setIsDownloading(true)
      const response = await apiClient.get('/reports/export-excel', {
        responseType: 'blob',
      })

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0]
      link.download = `untold-jd-report-${date}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Tải báo cáo thành công!')
    } catch (error) {
      console.error('Download report error:', error)
      handleError(error, { fallbackMessage: 'Lỗi khi tải báo cáo' })
    } finally {
      setIsDownloading(false)
    }
  }

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    return email?.[0]?.toUpperCase() || 'U'
  }

  const isHomePage = location.pathname === '/'

  return (
    <header className="md:px-20 md:py-6 w-full px-4 py-4">
      {/* Desktop: Split left/right, Mobile: Back/Bell/Avatar on first line */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Left Group on Desktop */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Back Button - Hidden on home page */}
          {!isHomePage && (
            <Button onClick={() => navigate(-1)} className={BUTTON_VARIANTS.icon}>
              <ArrowLeft className="md:w-5 md:h-5 w-4 h-4 text-white transition-colors duration-200" />
            </Button>
          )}

          {/* Home Button */}
          <Button onClick={handleHome} className={BUTTON_VARIANTS.icon}>
            <Home className="md:w-5 md:h-5 w-4 h-4 text-white transition-colors duration-200" />
          </Button>

          {/* Trending Button - Hidden on mobile, shown on desktop */}
          <Button
            onClick={onToggleTrending}
            className={`${BUTTON_VARIANTS.icon} md:flex hidden ${isTopTrending ? 'ring-2 ring-white ring-offset-2 ring-offset-primary' : ''}`}
          >
            <Trophy className="w-5 h-5" />
            <span>Top Trending</span>
          </Button>

          {/* Filter Dropdown - Hidden on mobile, shown on desktop */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button className={`${BUTTON_VARIANTS.icon} md:flex hidden`}>
                <Filter className="w-5 h-5" />
                <span>Filter</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-80 p-4 bg-white border-2 border-gray-200 shadow-2xl"
            >
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-900">
                    Business Unit
                  </label>
                  <Select
                    key={tempSelectedBU || 'empty'}
                    value={tempSelectedBU || undefined}
                    onValueChange={setTempSelectedBU}
                    onOpenChange={setIsBUSelectOpen}
                  >
                    <SelectTrigger className="hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/20 w-full h-10 border-2 border-gray-300">
                      <SelectValue placeholder="Chọn BU" />
                    </SelectTrigger>
                    <SelectContent
                      position="popper"
                      sideOffset={5}
                      className="max-h-80 w-[var(--radix-select-trigger-width)] bg-white border-2 border-gray-200 shadow-xl rounded-lg overflow-hidden p-0"
                    >
                      <div
                        className="sticky top-0 z-10 p-2 bg-white border-b border-gray-200"
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <div className="relative">
                          <Search className="left-3 top-1/2 absolute w-4 h-4 text-gray-400 -translate-y-1/2" />
                          <Input
                            ref={buSearchInputRef}
                            type="text"
                            placeholder="Tìm kiếm BU..."
                            value={buSearchQuery}
                            onChange={(e) => setBuSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="focus-visible:ring-2 focus-visible:ring-primary/20 h-9 w-full py-2 pl-10 pr-4 text-sm border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      <div className="max-h-60 py-1 overflow-auto">
                        {filteredDepartments.length > 0 ? (
                          filteredDepartments.map((department) => (
                            <SelectItem
                              key={department}
                              value={department}
                              className="px-4 py-3 hover:bg-purple-50 focus:bg-purple-50 cursor-pointer transition-colors data-[state=checked]:bg-purple-100 data-[state=checked]:font-semibold"
                            >
                              {department}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-sm text-center text-gray-500">
                            Không tìm thấy BU
                          </div>
                        )}
                      </div>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleApplyFilter} className="btn-primary flex-1">
                    Lọc
                  </Button>
                  <Button onClick={handleResetFilter} className="btn-secondary flex-1">
                    Reset
                  </Button>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Right Group on Desktop */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Bar - Desktop only */}
          <form onSubmit={handleSearchSubmit} className="md:block hidden">
            <div className="relative">
              <Search className="left-3 top-1/2 absolute w-4 h-4 text-gray-400 -translate-y-1/2" />
              <Input
                type="text"
                placeholder="Tìm kiếm JD..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:border-white placeholder:text-gray-400 h-9 w-64 py-2 pl-10 pr-4 text-sm text-gray-700 transition-all bg-white border-2 border-white rounded-full shadow-sm"
              />
            </div>
          </form>

          {/* Untold JD Button */}
          <Button
            onClick={handleLibraryJD}
            className="outline-ring/50 active:scale-95 hover:scale-105 flex items-center gap-2 h-9 md:h-11 px-3 md:px-4 text-xs md:text-sm font-semibold transition-all duration-300 ease-out border-2 rounded-full shadow-md bg-white text-primary border-primary hover:bg-[#FFEFF4]"
          >
            <BookCopy className="md:w-4 md:h-4 w-3 h-3" />
            <span>Untold JD của MoMoer</span>
          </Button>

          {/* Notification Bell */}
          <NotificationBell />

          {/* Avatar Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className={BUTTON_VARIANTS.avatar}>
                <div className="md:text-sm text-xs font-semibold text-white transition-colors duration-200">
                  {getInitials(user?.name, user?.email)}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-white border-2 border-gray-200 shadow-2xl"
            >
              {/* User Info Header */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center w-10 h-10 font-semibold text-white rounded-full">
                    {getInitials(user?.name, user?.email)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {user?.name || user?.email}
                    </p>
                    {user?.name && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-2">
                <DropdownMenuItem
                  onClick={handleMyJDs}
                  className="hover:bg-purple-50 px-4 py-3 transition-colors cursor-pointer"
                >
                  <FileText className="w-4 h-4 mr-3 text-purple-600" />
                  <span className="font-medium text-gray-900">JD của tôi</span>
                </DropdownMenuItem>

                {canExport && (
                  <>
                    <DropdownMenuItem
                      onClick={handleDownloadReport}
                      disabled={isDownloading}
                      className="hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-3 transition-colors cursor-pointer"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-3 text-green-600" />
                      <span className="font-medium text-gray-900">
                        {isDownloading ? 'Đang tải...' : 'Tải báo cáo Excel'}
                      </span>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="my-1 bg-gray-200" />
                  </>
                )}

                {!canExport && <DropdownMenuSeparator className="my-1 bg-gray-200" />}

                <DropdownMenuItem
                  onClick={logout}
                  className="hover:bg-red-50 px-4 py-3 transition-colors cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-3 text-red-600" />
                  <span className="font-medium text-red-600">Đăng xuất</span>
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile Second Row: Search Bar (full width) */}
      <div className="md:hidden mt-3">
        <form onSubmit={handleSearchSubmit} className="w-full">
          <div className="relative">
            <Search className="left-3 top-1/2 absolute w-4 h-4 text-gray-400 -translate-y-1/2" />
            <Input
              type="text"
              placeholder="Tìm kiếm JD..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:border-white placeholder:text-gray-400 h-9 w-full py-2 pl-10 pr-4 text-sm text-gray-700 transition-all bg-white border-2 border-white rounded-full shadow-sm"
            />
          </div>
        </form>
      </div>

      {/* Mobile Third Row: Trending & Filter */}
      <div className="md:hidden flex items-center gap-2 mt-2">
        {/* Trending Button */}
        <Button
          onClick={onToggleTrending}
          className={`${BUTTON_VARIANTS.icon} ${isTopTrending ? 'ring-2 ring-white ring-offset-2 ring-offset-primary' : ''}`}
        >
          <Trophy className="w-4 h-4" />
          <span>Trending</span>
        </Button>

        {/* Filter Dropdown - Mobile */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button className={BUTTON_VARIANTS.icon}>
              <Filter className="w-4 h-4" />
              <span>Filter</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-80 p-4 bg-white border-2 border-gray-200 shadow-2xl"
          >
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-900">
                  Business Unit
                </label>
                <Select
                  key={tempSelectedBU || 'empty'}
                  value={tempSelectedBU || undefined}
                  onValueChange={setTempSelectedBU}
                  onOpenChange={setIsBUSelectOpen}
                >
                  <SelectTrigger className="hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/20 w-full h-10 border-2 border-gray-300">
                    <SelectValue placeholder="Chọn BU" />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    sideOffset={5}
                    className="max-h-80 w-[var(--radix-select-trigger-width)] bg-white border-2 border-gray-200 shadow-xl rounded-lg overflow-hidden p-0"
                  >
                    <div
                      className="sticky top-0 z-10 p-2 bg-white border-b border-gray-200"
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <div className="relative">
                        <Search className="left-3 top-1/2 absolute w-4 h-4 text-gray-400 -translate-y-1/2" />
                        <Input
                          ref={buSearchInputRef}
                          type="text"
                          placeholder="Tìm kiếm BU..."
                          value={buSearchQuery}
                          onChange={(e) => setBuSearchQuery(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="focus-visible:ring-2 focus-visible:ring-primary/20 h-9 w-full py-2 pl-10 pr-4 text-sm border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                    <div className="max-h-60 py-1 overflow-auto">
                      {filteredDepartments.length > 0 ? (
                        filteredDepartments.map((department) => (
                          <SelectItem
                            key={department}
                            value={department}
                            className="px-4 py-3 hover:bg-purple-50 focus:bg-purple-50 cursor-pointer transition-colors data-[state=checked]:bg-purple-100 data-[state=checked]:font-semibold"
                          >
                            {department}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-center text-gray-500">
                          Không tìm thấy BU
                        </div>
                      )}
                    </div>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleApplyFilter} className="btn-primary flex-1">
                  Lọc
                </Button>
                <Button onClick={handleResetFilter} className="btn-secondary flex-1">
                  Reset
                </Button>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

export default LibraryJDHeader
